import { nationalityName } from '@felix/domain';
import { ActionIcon, Button, Menu, Skeleton, Tabs, Text } from '@mantine/core';
import {
  IconArrowBackUp,
  IconArrowsExchange,
  IconCertificate,
  IconChevronDown,
  IconClockHour4,
  IconDots,
  IconDownload,
  IconEdit,
  IconExternalLink,
  IconFileCertificate,
  IconFileText,
  IconHistory,
  IconKey,
  IconLogin,
  IconLogout,
  IconMail,
  IconPencil,
  IconPlus,
  IconPrinter,
  IconShieldOff,
  IconTrash,
  IconUpload,
  IconUserCheck,
  IconUserOff,
  IconX,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { PersonAvatar } from '../../PersonAvatar.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { setupErrorText } from '../settings/RolesScreen.js';
import { setupErrorCode, setupErrorDetail } from '../settings/setup-source.js';
import {
  ChangeEmailDialog,
  PasswordConfirm,
  type TemporaryLogin,
} from '../settings/UsersScreen.js';
import { CreateLoginDialog, CrewCardDialog, RemoveCardDialog } from './CrewCardDialog.js';
import { CREW_FILE_ACCEPT, CrewFileViewer, crewFileErrorKey } from './CrewFileViewer.js';
import { CrewNotesPanel } from './CrewNotesPanel.js';
import { MedicalPanel, PhysicalPanel } from './CrewParticulars.js';
import classes from './CrewProfileScreen.module.css';
import { assignmentActions, cardRowMovable, movementTarget } from './card-movements.js';
import { type PdfMode, usePersonnelExport } from './crew-exports.js';
import {
  type AssignmentState,
  assignmentState,
  type ProfileTab,
  profileTabs,
} from './crew-profile.js';
import {
  AmendDialog,
  HistoryDialog,
  RankChangeDialog,
  ReasonDialog,
  SignOffDialog,
  SignOnDialog,
} from './MovementDialogs.js';
import { canIssueEmploymentRecord } from './record-of-employment-pdf.js';
import { type MovementAction, movementActionHint } from './sign-on-off.js';
import { type CrewCardEditor, errorCode, type PersonnelSource } from './source.js';
import type {
  ContractStatus,
  CrewAssignment,
  CrewFileRecord,
  CrewMedicalResult,
  CrewProfileView,
  CrewStatus,
} from './types.js';

export { assignmentActions, cardRowMovable, movementTarget };

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; view: CrewProfileView };

const STATUS_TONE: Record<CrewStatus, StatusTone> = {
  on_board: 'success',
  ashore: 'info',
  not_assigned: 'neutral',
};

const STATE_TONE: Record<AssignmentState | ContractStatus, StatusTone> = {
  active: 'success',
  completed: 'neutral',
  planned: 'info',
  overdue: 'danger',
};

const ACTION_ICONS: Record<MovementAction, typeof IconEdit> = {
  signOff: IconLogout,
  rankChange: IconArrowsExchange,
  amend: IconEdit,
  cancel: IconX,
  revoke: IconArrowBackUp,
  history: IconHistory,
};

/** Etiket ve değer satırı (tasarım 3: solda gri etiket, sağda değer). */
function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className={classes.field}>
      <dt>{label}</dt>
      <dd>{value === null || value === undefined || value === '' ? '—' : value}</dd>
    </div>
  );
}

export interface CrewProfileBodyProps {
  view: CrewProfileView;
  scope: 'web' | 'ship';
  tab: ProfileTab;
  onTab: (tab: ProfileTab) => void;
  /** Uygulama içi gezinme (Documents, Training, Hours of Rest; `?user=`). */
  onNavigate?: ((path: string) => void) | undefined;
  /** Yalnız ofiste ve yetkiliyse. */
  onEdit?: (() => void) | undefined;
  onCreateLogin?: (() => void) | undefined;
  /** Yalnız ofiste, hesaplı kişide ve yetkiliyse: yeni geçici parola (ortak 2026-10-30). */
  onResetPassword?: (() => void) | undefined;
  /** Yalnız ofiste, hesaplı kişide ve yetkiliyse: hesap işlemleri (`cardAccountActions`). */
  accountActions?: readonly CardAccountAction[] | undefined;
  onAccountAction?: ((action: CardAccountAction) => void) | undefined;
  /** Yalnız ofiste ve yetkiliyse: yanlış açılan kartı kaldırır (ADR 0142 §6). */
  onRemove?: (() => void) | undefined;
  /** Yalnız ofiste ve yetkiliyse: Sign on ("New Contract"). */
  onSignOn?: (() => void) | undefined;
  /**
   * Yalnız ofiste ve `users.edit` ise: atamanın Joining / Sign-Off işlemi ve History. Hareket
   * işlemleri satır başına `cardRowMovable` ile (kendi kartı, yönetici rütbesi; ADR 0151 §4).
   */
  onMovement?: ((action: MovementAction, row: CrewAssignment) => void) | undefined;
  /** Crew Profile PDF'i (ADR 0131 Ek 3): yazdır ya da indir; ofiste ve gemide. */
  onPrint?: ((mode: PdfMode) => void) | undefined;
  printing?: boolean;
  /** Ayrılışı tamamlanmış atamanın Record of Employment PDF'i [MLC A2.1 §1(e)]. */
  onEmploymentRecord?: ((row: CrewAssignment) => void) | undefined;
  /**
   * Yalnız ofiste ve `crew_medical.view` ise: sağlık bilgisini okur (her okuma kayıtlı; ADR 0158
   * §2). Verilmezse bölüm yok.
   */
  onLoadMedical?: (() => Promise<CrewMedicalResult>) | undefined;
  /**
   * Office notes sekmesinin içeriği (ADR 0159): yalnız web ofiste, `crew_notes.view` hücresiyle
   * verilir. Verilmezse sekme yoktur (gemi, yetkisiz kullanıcı).
   */
  notes?: ReactNode;
  /**
   * Contract sekmesinde atamanın belgeleri (ADR 0164): yalnız web ofiste. `url` belgenin adresi
   * (açılış kayda geçer); `upload` personel kartı düzenleme yetkisiyle verilir. Verilmezse sütun
   * yok (gemi: belgeler gemiye inmez).
   */
  contractFiles?:
    | {
        url: (fileId: string) => string;
        upload?: ((row: CrewAssignment, file: File) => Promise<void>) | undefined;
      }
    | undefined;
}

/**
 * Crew Profile gövdesi (`gemi-personel.jpeg` panel 3; ADR 0131 §2): başlık kartı (ad, rütbe, durum,
 * iletişim ve kimlik özeti), sekmeler, bölümler. Gemide salt okunur: Edit, "Create login",
 * iletişim ve acil durum kişisi yok (gemiye inmez; KVKK md. 4).
 */
export function CrewProfileBody({
  view,
  scope,
  tab,
  onTab,
  onNavigate,
  onEdit,
  onCreateLogin,
  onResetPassword,
  accountActions = [],
  onAccountAction,
  onRemove,
  onSignOn,
  onMovement,
  onPrint,
  printing = false,
  onEmploymentRecord,
  onLoadMedical,
  notes,
  contractFiles,
}: CrewProfileBodyProps) {
  const account = onAccountAction ? accountActions : [];
  const { t, i18n } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`crewProfile.${key}`, options);
  const locale = i18n.resolvedLanguage ?? 'en';
  const web = scope === 'web';
  const profile = view.profile;
  const nationality = nationalityName(profile?.nationality, locale);
  const today = view.today ?? new Date().toISOString().slice(0, 10);
  const open = (path: string) => onNavigate?.(`${path}?user=${encodeURIComponent(view.userId)}`);
  const withNotes = web && notes !== undefined && notes !== null;
  const tabs = profileTabs({ notes: withNotes });
  const current = view.assignments.find((row) => row.validTo === null && row.validFrom <= today);
  const m = (key: string, options: Record<string, unknown> = {}) => t(`signOnOff.${key}`, options);

  const header = (
    <Panel aria-label={view.fullName}>
      <div className={classes.header}>
        <div className={classes.identity}>
          <PersonAvatar name={view.fullName} size={96} src={view.photoUrl ?? null} />
          <div className={classes.who}>
            <h2 className={classes.name}>{view.fullName}</h2>
            <span className={classes.rank}>{view.rank ?? '—'}</span>
            <StatusBadge tone={STATUS_TONE[view.status]} outlined>
              {k(`status.${view.status}`)}
            </StatusBadge>
          </div>
        </div>
        <dl className={classes.summary}>
          {web ? (
            <>
              <Field
                label={k('header.login')}
                value={
                  view.hasLogin && view.email ? (
                    <>
                      {view.email}
                      {/* Hesap durumu (ortak 2026-10-30): etkin ya da devre dışı */}
                      <span className={classes.loginState}>
                        <StatusBadge tone={view.active ? 'success' : 'danger'} variant="dot">
                          {view.active ? k('account.active') : k('account.disabled')}
                        </StatusBadge>
                      </span>
                      {/* Geçici parola henüz değişmedi (ortak 2026-10-30 madde 9) */}
                      {view.active && view.mustChangePassword ? (
                        <span className={classes.loginState}>
                          <StatusBadge tone="warning" variant="dot">
                            {k('account.mustChange')}
                          </StatusBadge>
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <>
                      {k('noLogin')}
                      {/* Hesapsız pasif kişi (eski veri): Create login onu etkinleştirmez
                          (ADR 0150 §5); pasiflik hesap açılmadan önce de görünür */}
                      {view.active ? null : (
                        <span className={classes.loginState}>
                          <StatusBadge tone="danger" variant="dot">
                            {k('account.inactive')}
                          </StatusBadge>
                        </span>
                      )}
                    </>
                  )
                }
              />
              <Field label={k('header.phone')} value={profile?.phone} />
            </>
          ) : null}
          <Field label={k('header.nationality')} value={nationality} />
          <Field label={k('header.dateOfBirth')} value={formatDay(profile?.dateOfBirth ?? null)} />
          <Field label={k('header.crewCode')} value={profile?.crewCode} />
        </dl>
        <div className={classes.tools}>
          {onSignOn && !current ? (
            <Button size="xs" leftSection={<IconLogin size={14} />} onClick={onSignOn}>
              {k('signOn')}
            </Button>
          ) : null}
          {onMovement && current && cardRowMovable(view, current) ? (
            <Button
              variant="default"
              size="xs"
              leftSection={<IconLogout size={14} />}
              onClick={() => onMovement('signOff', current)}
            >
              {k('signOff')}
            </Button>
          ) : null}
          {onEdit ? (
            <Button
              variant="default"
              size="xs"
              leftSection={<IconPencil size={14} />}
              onClick={onEdit}
            >
              {k('edit')}
            </Button>
          ) : null}
          {/* Giriş vermenin tek, açık düğmesi (ortak 2026-10-30 madde 9); More menüsünde de var */}
          {onCreateLogin ? (
            <Button
              variant="default"
              size="xs"
              leftSection={<IconKey size={14} />}
              onClick={onCreateLogin}
            >
              {k('createLogin')}
            </Button>
          ) : onResetPassword ? (
            <Button
              variant="default"
              size="xs"
              leftSection={<IconKey size={14} />}
              onClick={onResetPassword}
            >
              {k('newPassword')}
            </Button>
          ) : null}
          {onPrint ? (
            <Button
              variant="default"
              size="xs"
              leftSection={<IconPrinter size={14} />}
              loading={printing}
              aria-label={k('printFor', { name: view.fullName })}
              onClick={() => onPrint('print')}
            >
              {k('print')}
            </Button>
          ) : null}
          {onNavigate ||
          onCreateLogin ||
          onResetPassword ||
          account.length > 0 ||
          onRemove ||
          onPrint ? (
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <Button
                  size="xs"
                  rightSection={<IconChevronDown size={14} />}
                  aria-label={k('moreFor', { name: view.fullName })}
                >
                  {k('more')}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {onPrint ? (
                  <Menu.Item
                    leftSection={<IconDownload size={16} />}
                    disabled={printing}
                    onClick={() => onPrint('save')}
                  >
                    {k('downloadPdf')}
                  </Menu.Item>
                ) : null}
                {onCreateLogin ? (
                  <Menu.Item leftSection={<IconKey size={16} />} onClick={onCreateLogin}>
                    {k('createLogin')}
                  </Menu.Item>
                ) : null}
                {account.includes('changeEmail') ? (
                  <Menu.Item
                    leftSection={<IconMail size={16} />}
                    onClick={() => onAccountAction?.('changeEmail')}
                  >
                    {k('account.changeEmail')}
                  </Menu.Item>
                ) : null}
                {onResetPassword ? (
                  <Menu.Item leftSection={<IconKey size={16} />} onClick={onResetPassword}>
                    {k('resetPassword')}
                  </Menu.Item>
                ) : null}
                {account.includes('resetTotp') ? (
                  <Menu.Item
                    leftSection={<IconShieldOff size={16} />}
                    onClick={() => onAccountAction?.('resetTotp')}
                  >
                    {k('account.resetTotp')}
                  </Menu.Item>
                ) : null}
                {account.includes('enable') ? (
                  <Menu.Item
                    leftSection={<IconUserCheck size={16} />}
                    onClick={() => onAccountAction?.('enable')}
                  >
                    {k('account.enable')}
                  </Menu.Item>
                ) : null}
                {account.includes('disable') ? (
                  <Menu.Item
                    color="red"
                    leftSection={<IconUserOff size={16} />}
                    onClick={() => onAccountAction?.('disable')}
                  >
                    {k('account.disable')}
                  </Menu.Item>
                ) : null}
                {onNavigate ? (
                  <>
                    <Menu.Item
                      leftSection={<IconCertificate size={16} />}
                      onClick={() => open('/crew/certificates')}
                    >
                      {k('openCertificates')}
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconClockHour4 size={16} />}
                      onClick={() => open('/crew/rest-hours')}
                    >
                      {k('openRestHours')}
                    </Menu.Item>
                  </>
                ) : null}
                {onRemove ? (
                  <>
                    <Menu.Divider />
                    <Menu.Item color="red" leftSection={<IconTrash size={16} />} onClick={onRemove}>
                      {k('removeCard')}
                    </Menu.Item>
                  </>
                ) : null}
              </Menu.Dropdown>
            </Menu>
          ) : null}
        </div>
      </div>
    </Panel>
  );

  const overview = (
    <div className={classes.sections}>
      <Panel title={k('sections.personal')} divider>
        <dl className={classes.fields}>
          <Field label={k('fields.fullName')} value={view.fullName} />
          <Field label={k('fields.crewCode')} value={profile?.crewCode} />
          <Field label={k('fields.rank')} value={view.rank} />
          <Field label={k('fields.vessel')} value={view.vessel?.name} />
          <Field label={k('fields.nationality')} value={nationality} />
          <Field label={k('fields.dateOfBirth')} value={formatDay(profile?.dateOfBirth ?? null)} />
          <Field label={k('fields.placeOfBirth')} value={profile?.placeOfBirth} />
          <Field
            label={k('fields.gender')}
            value={profile?.gender ? k(`gender.${profile.gender}`) : null}
          />
          {web ? (
            <Field
              label={k('fields.maritalStatus')}
              value={profile?.maritalStatus ? k(`marital.${profile.maritalStatus}`) : null}
            />
          ) : null}
        </dl>
      </Panel>
      {web ? (
        <div className={classes.side}>
          <Panel title={k('sections.contact')} divider>
            <dl className={classes.fields}>
              <Field label={k('fields.contactEmail')} value={profile?.contactEmail} />
              <Field label={k('fields.phone')} value={profile?.phone} />
              <Field label={k('fields.address')} value={profile?.address} />
            </dl>
          </Panel>
          <Panel title={k('sections.emergency')} divider>
            <dl className={classes.fields}>
              <Field label={k('fields.emergencyName')} value={profile?.emergencyName} />
              <Field label={k('fields.emergencyPhone')} value={profile?.emergencyPhone} />
              <Field label={k('fields.emergencyRelation')} value={profile?.emergencyRelation} />
            </dl>
          </Panel>
          {/* Kıyafet ve fiziksel özellikler, sağlık bilgisi (ADR 0158): yalnız ofiste */}
          <PhysicalPanel profile={profile} />
          {onLoadMedical ? <MedicalPanel key={view.userId} load={onLoadMedical} /> : null}
        </div>
      ) : null}
    </div>
  );

  const employable = (row: CrewAssignment) =>
    onEmploymentRecord !== undefined && canIssueEmploymentRecord(row, today);
  const withActions = onMovement !== undefined || onEmploymentRecord !== undefined;
  // Kontrat belgeleri (ADR 0164): yalnız ofiste; gemide sütun yok
  const withFiles = web && contractFiles !== undefined;
  const fileInput = useRef<HTMLInputElement>(null);
  const [viewing, setViewing] = useState<CrewFileRecord | null>(null);
  const [uploadRow, setUploadRow] = useState<CrewAssignment | null>(null);
  const [upload, setUpload] = useState<
    { kind: 'busy' } | { kind: 'done' } | { kind: 'error'; code: string } | null
  >(null);
  const startUpload = (row: CrewAssignment) => {
    setUploadRow(row);
    fileInput.current?.click();
  };
  const onFileChosen = async (file: File | undefined) => {
    const row = uploadRow;
    if (fileInput.current) fileInput.current.value = '';
    if (!file || !row || !contractFiles?.upload) return;
    setUpload({ kind: 'busy' });
    try {
      await contractFiles.upload(row, file);
      setUpload({ kind: 'done' });
    } catch (error) {
      setUpload({ kind: 'error', code: errorCode(error) });
    }
  };
  const filesCell = (row: CrewAssignment) => (
    <td>
      <div className={classes.contractFiles}>
        {(row.files ?? []).length === 0 ? (
          <span className={classes.date}>{t('crewFiles.contract.none')}</span>
        ) : (
          (row.files ?? []).map((file) => (
            <Button
              key={file.id}
              size="compact-xs"
              variant="subtle"
              leftSection={<IconFileText size={14} />}
              aria-label={t('crewFiles.open', { name: file.name })}
              title={file.name}
              onClick={() => setViewing(file)}
            >
              <span className={classes.contractFileName}>{file.name}</span>
            </Button>
          ))
        )}
        {contractFiles?.upload ? (
          <ActionIcon
            variant="subtle"
            size="sm"
            aria-label={t('crewFiles.contract.uploadFor', { vessel: row.vesselName })}
            disabled={upload?.kind === 'busy'}
            onClick={() => startUpload(row)}
          >
            <IconUpload size={14} />
          </ActionIcon>
        ) : null}
      </div>
    </td>
  );
  const rowMenu = (row: CrewAssignment) =>
    onMovement || employable(row) ? (
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label={m('actionsFor', { name: row.vesselName })}
          >
            <IconDots size={18} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {onEmploymentRecord && employable(row) ? (
            <Menu.Item
              leftSection={<IconFileCertificate size={16} />}
              disabled={printing}
              aria-label={k('employment.actionFor', { vessel: row.vesselName })}
              onClick={() => onEmploymentRecord(row)}
            >
              {k('employment.action')}
            </Menu.Item>
          ) : null}
          {onMovement
            ? assignmentActions(
                row,
                today,
                view.canVoid === true,
                view.canRankChange !== false,
                cardRowMovable(view, row),
              ).map((action) => {
                const Icon = ACTION_ICONS[action];
                const hint = movementActionHint(action, row, today);
                return (
                  <Menu.Item
                    key={action}
                    {...(action === 'cancel' || action === 'revoke' ? { color: 'red' } : {})}
                    leftSection={<Icon size={16} />}
                    disabled={hint !== null}
                    onClick={() => onMovement(action, row)}
                  >
                    {m(`action.${action}`)}
                    {hint ? (
                      <Text component="span" size="xs" c="dimmed" display="block">
                        {m(`action.${hint}`)}
                      </Text>
                    ) : null}
                  </Menu.Item>
                );
              })
            : null}
        </Menu.Dropdown>
      </Menu>
    ) : null;
  const port = (name?: string | null, code?: string | null) =>
    name || code ? (
      <span className={classes.port}>
        <span>{name ?? code}</span>
        {name && code ? <span className={classes.date}>{code}</span> : null}
      </span>
    ) : (
      '—'
    );
  const stateOf = (row: CrewAssignment) => row.contractStatus ?? assignmentState(row, today);
  const newContract = onSignOn ? (
    <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={onSignOn}>
      {k('assignments.newContract')}
    </Button>
  ) : null;

  const assignments = (kind: 'contracts' | 'joining') =>
    view.assignments.length === 0 ? (
      <Panel>
        <EmptyState
          title={k('assignments.empty')}
          description={k('assignments.readOnly')}
          action={newContract}
        />
      </Panel>
    ) : (
      <Panel flush>
        <div className={classes.noteRow}>
          <p className={classes.note}>
            {k('assignments.readOnly')}
            {kind === 'contracts' && withFiles ? (
              <>
                <br />
                {t('crewFiles.contract.officeOnly')}
              </>
            ) : null}
          </p>
          {kind === 'contracts' ? newContract : null}
        </div>
        {kind === 'contracts' && withFiles ? (
          <>
            {contractFiles?.upload ? (
              <input
                ref={fileInput}
                type="file"
                hidden
                accept={CREW_FILE_ACCEPT}
                aria-label={t('crewFiles.contract.upload')}
                onChange={(event) => void onFileChosen(event.currentTarget.files?.[0])}
              />
            ) : null}
            {upload?.kind === 'error' ? (
              <p className={classes.note} data-tone="danger" role="alert">
                {t(crewFileErrorKey(upload.code))}
              </p>
            ) : upload ? (
              <p className={classes.note} role="status">
                {upload.kind === 'busy' ? t('crewFiles.uploading') : t('crewFiles.uploaded')}
              </p>
            ) : null}
            <CrewFileViewer
              file={viewing}
              url={contractFiles?.url ?? (() => '')}
              onClose={() => setViewing(null)}
            />
          </>
        ) : null}
        <section className={classes.tableWrap} aria-label={k(`assignments.${kind}Table`)}>
          <table className={classes.table}>
            <thead>
              {kind === 'contracts' ? (
                <tr>
                  <th scope="col">{k('assignments.contractNo')}</th>
                  <th scope="col">{k('assignments.vessel')}</th>
                  <th scope="col">{k('assignments.rank')}</th>
                  <th scope="col">{k('assignments.start')}</th>
                  <th scope="col">{k('assignments.end')}</th>
                  <th scope="col">{k('assignments.status')}</th>
                  {withFiles ? <th scope="col">{t('crewFiles.contract.column')}</th> : null}
                  {withActions ? <th scope="col">{k('assignments.actions')}</th> : null}
                </tr>
              ) : (
                <tr>
                  <th scope="col">{k('assignments.vessel')}</th>
                  <th scope="col">{k('assignments.rank')}</th>
                  <th scope="col">{k('assignments.joined')}</th>
                  <th scope="col">{k('assignments.joinPort')}</th>
                  <th scope="col">{k('assignments.signedOff')}</th>
                  <th scope="col">{k('assignments.signOffPort')}</th>
                  <th scope="col">{k('assignments.reason')}</th>
                  {withActions ? <th scope="col">{k('assignments.actions')}</th> : null}
                </tr>
              )}
            </thead>
            <tbody>
              {view.assignments.map((row) => {
                const state = stateOf(row);
                return kind === 'contracts' ? (
                  <tr key={row.id}>
                    <td>{row.contractNo ?? '—'}</td>
                    <td className={classes.strong}>{row.vesselName}</td>
                    <td>{row.rank ?? '—'}</td>
                    <td className={classes.date}>{formatDay(row.validFrom)}</td>
                    <td className={classes.date}>{formatDay(row.contractEnd ?? row.validTo)}</td>
                    <td>
                      <StatusBadge tone={STATE_TONE[state]}>
                        {k(`assignments.state.${state}`)}
                      </StatusBadge>
                    </td>
                    {withFiles ? filesCell(row) : null}
                    {withActions ? <td>{rowMenu(row)}</td> : null}
                  </tr>
                ) : (
                  <tr key={row.id}>
                    <td className={classes.strong}>{row.vesselName}</td>
                    <td>{row.rank ?? '—'}</td>
                    <td className={classes.date}>{formatDay(row.validFrom)}</td>
                    <td>{port(row.joinPort, row.joinPortCode)}</td>
                    <td className={classes.date}>{formatDay(row.validTo)}</td>
                    <td>{port(row.signOffPort, row.signOffPortCode)}</td>
                    <td>{row.signOffReason ? m(`reason.${row.signOffReason}`) : '—'}</td>
                    {withActions ? <td>{rowMenu(row)}</td> : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      </Panel>
    );

  return (
    <div className={classes.page}>
      {header}
      {web && !view.hasLogin ? (
        <p className={classes.notice}>
          <IconUserOff size={16} aria-hidden />
          <span>{k('noLoginHelp')}</span>
        </p>
      ) : null}
      {web ? null : <p className={classes.notice}>{k('shipReadOnly')}</p>}
      <Tabs
        value={tab}
        onChange={(value) => {
          const target = tabs.find((item) => item.id === value);
          if (!target) return;
          if (target.link) open(target.link);
          else onTab(target.id);
        }}
        keepMounted={false}
        classNames={{ list: classes.tabs }}
      >
        <Tabs.List aria-label={k('tabs.label')}>
          {tabs.map((item) => (
            <Tabs.Tab
              key={item.id}
              value={item.id}
              rightSection={item.link ? <IconExternalLink size={13} aria-hidden /> : null}
              disabled={item.link !== null && !onNavigate}
            >
              {item.id === 'notes' ? t('crewNotes.tab') : k(`tabs.${item.id}`)}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        <Tabs.Panel value="overview" pt="md">
          {overview}
        </Tabs.Panel>
        <Tabs.Panel value="contracts" pt="md">
          {assignments('contracts')}
        </Tabs.Panel>
        <Tabs.Panel value="joining" pt="md">
          {assignments('joining')}
        </Tabs.Panel>
        {withNotes ? (
          <Tabs.Panel value="notes" pt="md">
            {notes}
          </Tabs.Panel>
        ) : null}
      </Tabs>
    </div>
  );
}

/**
 * Kartta "Reset password" (ortak 2026-10-30): hesaplı kişide, sunucu yetki verdiyse
 * (`canResetPassword`, Settings ▸ Users ile aynı `users.void` + `users.edit`) ve kaynak ucu
 * biliyorsa; `users` satırının sürümüyle Settings ▸ Users'daki uca gider. Gemide kartı yazan kaynak
 * yok.
 */
export function cardPasswordReset(
  view: CrewProfileView,
  editor: CrewCardEditor | undefined,
): ((password: string) => Promise<string>) | undefined {
  const reset = editor?.resetPassword;
  if (!reset || !view.hasLogin || !view.canResetPassword) return undefined;
  return async (password) =>
    (await reset.call(editor, view.userId, view.rowVersion, password)).temporaryPassword;
}

/** Kartın hesap işlemleri (ortak 2026-10-30): Settings ▸ Users'daki aynı uçlar. */
export type CardAccountAction = 'changeEmail' | 'resetTotp' | 'disable' | 'enable';

/**
 * Kartın More menüsündeki hesap işlemleri: hesaplı kişide, sunucunun verdiği yetkiyle
 * (`canChangeEmail` → `users.edit`, ADR 0145; `canManageLogin` → `users.void` + `users.edit`, ADR
 * 0042 §23, ADR 0151 Ek) ve kaynak ucu biliyorsa. İki adımlı doğrulama zaten kapalıysa kapatma yok
 * (ADR 0148). Gemide kaynak yok.
 */
export function cardAccountActions(
  view: CrewProfileView,
  editor: CrewCardEditor | undefined,
): CardAccountAction[] {
  if (!editor || !view.hasLogin) return [];
  const actions: CardAccountAction[] = [];
  if (view.canChangeEmail && editor.changeEmail) actions.push('changeEmail');
  if (view.canManageLogin) {
    if (view.totpEnrolled && editor.resetTotp) actions.push('resetTotp');
    // Kişi kendini devre dışı bırakamaz (LAST_ADMIN self; ADR 0151 §3); eski sunucu göndermez
    const toggle = view.active ? (view.canDisableLogin === false ? null : 'disable') : 'enable';
    if (editor.setLoginActive && toggle) actions.push(toggle);
  }
  return actions;
}

/**
 * Crew ▸ Crew Profile (`/crew/profile/:userId`; ADR 0131 §2): ofiste ve gemide aynı ekran.
 * Ofiste Edit, "Create login" ve "Reset password" (yetkiye göre); gemide salt okunur.
 */
export function CrewProfileScreen({ source, userId }: { source: PersonnelSource; userId: string }) {
  const { t } = useTranslation();
  const shell = useShell();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [tab, setTab] = useState<ProfileTab>('overview');
  const [dialog, setDialog] = useState<
    'edit' | 'login' | 'reset' | 'remove' | CardAccountAction | null
  >(null);
  /** Sıfırlanan geçici parola: pencerede bir kez gösterilir, kapanınca bellekten düşer. */
  const [temporary, setTemporary] = useState<TemporaryLogin | null>(null);
  const [movement, setMovement] = useState<
    { action: 'signOn' } | { action: MovementAction; row: CrewAssignment } | null
  >(null);
  const [vessels, setVessels] = useState<{ value: string; label: string }[]>([]);
  const exporter = usePersonnelExport(source);
  const [exportError, setExportError] = useState<string | null>(null);
  const exported = (run: Promise<string | null>) => {
    setExportError(null);
    void run.then(setExportError);
  };

  const load = useCallback(() => {
    setState({ kind: 'loading' });
    source
      .crewProfile(userId)
      .then((view) => setState({ kind: 'ready', view }))
      .catch((error: unknown) => setState({ kind: 'error', code: errorCode(error) }));
  }, [source, userId]);
  useEffect(load, [load]);

  const view = state.kind === 'ready' ? state.view : null;
  const editor = source.scope === 'web' ? source.crewCards : undefined;
  // Sağlık bilgisi (ADR 0158 §2): yalnız ofiste, istenince okunur
  const loadMedical = editor?.medical;
  const resetPassword = view ? cardPasswordReset(view, editor) : undefined;
  const accountActions = view ? cardAccountActions(view, editor) : [];
  const [accountNotice, setAccountNotice] = useState<string | null>(null);
  /** Hesap işlemi bitti: bildirim, kart yeniden okunur (satır sürümü değişti). */
  const accountDone = (notice: string) => {
    setDialog(null);
    setAccountNotice(notice);
    load();
  };
  const accountError = (error: unknown) =>
    setupErrorText(t, setupErrorCode(error), setupErrorDetail(error));
  const movements = source.scope === 'web' ? source.movementEditor : undefined;
  // Ofis notları yalnız web ofis kaynağında (ADR 0159); gemi kaynağında hiç yok
  const notesSource = source.scope === 'web' ? source.crewNotes : undefined;
  const signOn = () => {
    setMovement({ action: 'signOn' });
    source
      .vessels()
      .then((list) => setVessels(list.map((vessel) => ({ value: vessel.id, label: vessel.name }))))
      .catch(() => setVessels([]));
  };
  const moved = () => {
    setMovement(null);
    load();
  };
  const target =
    view && movement && movement.action !== 'signOn'
      ? movementTarget(view, movement.row, movement.row.validTo === null ? 'joining' : 'sign_off')
      : null;
  const viewToday = view?.today ?? new Date().toISOString().slice(0, 10);
  const header = (
    <PageHeader
      title={t('crewProfile.title')}
      titleHidden
      // Menüde değil (Crew List altı): kendi yardım içeriği (ADR 0131 Ek 3)
      helpKey="crewProfile"
      crumb={view?.fullName ?? t('crewProfile.title')}
    />
  );

  if (state.kind === 'error') {
    return (
      <div className={classes.page}>
        {header}
        <Panel>
          <EmptyState
            tone="danger"
            title={state.code === 'NOT_FOUND' ? t('crewProfile.notFound') : t('crewProfile.error')}
            description={state.code === 'NOT_FOUND' ? undefined : t('common.loadError')}
            action={
              state.code === 'NOT_FOUND' ? (
                shell ? (
                  <Button size="xs" variant="light" onClick={() => shell.navigate('/crew')}>
                    {t('crewProfile.back')}
                  </Button>
                ) : null
              ) : (
                <Button size="xs" variant="light" onClick={load}>
                  {t('common.retry')}
                </Button>
              )
            }
          />
        </Panel>
      </div>
    );
  }

  return (
    <div className={classes.page}>
      {header}
      {exportError ? (
        <p role="alert" className={classes.notice}>
          {exportError}
        </p>
      ) : null}
      {accountNotice ? (
        <p role="status" className={classes.notice} data-tone="success">
          {accountNotice}
        </p>
      ) : null}
      {view ? (
        <CrewProfileBody
          view={view}
          scope={source.scope}
          tab={tab}
          onTab={setTab}
          onNavigate={shell?.navigate}
          onEdit={editor && view.canEdit ? () => setDialog('edit') : undefined}
          onCreateLogin={editor && view.canCreateLogin ? () => setDialog('login') : undefined}
          onResetPassword={resetPassword ? () => setDialog('reset') : undefined}
          accountActions={accountActions}
          onAccountAction={(action) => {
            setAccountNotice(null);
            setDialog(action);
          }}
          onRemove={editor?.remove && view.canRemove ? () => setDialog('remove') : undefined}
          onSignOn={movements && view.canSignOn && view.profile ? signOn : undefined}
          // History her `users.edit` sahibine açık; hareketler satır başına (`cardRowMovable`)
          onMovement={
            movements && view.canEdit ? (action, row) => setMovement({ action, row }) : undefined
          }
          onPrint={(mode) => exported(exporter.profile(view, mode))}
          printing={exporter.busy}
          onEmploymentRecord={(row) => exported(exporter.employment(view, row, 'save'))}
          onLoadMedical={
            loadMedical && view.canViewMedical ? () => loadMedical(view.userId) : undefined
          }
          // Kontrat belgeleri (ADR 0164): yalnız ofiste; yükleme kartı düzenleyene. Yüklemeden
          // sonra kart sessizce yeniden okunur (yükleniyor durumuna düşmez, bildirim kalır)
          contractFiles={
            editor?.contractFileUrl
              ? {
                  url: editor.contractFileUrl,
                  upload:
                    editor.uploadContractFile && view.canEdit
                      ? async (row, file) => {
                          await editor.uploadContractFile?.(row.id, file);
                          const fresh = await source.crewProfile(view.userId);
                          setState({ kind: 'ready', view: fresh });
                        }
                      : undefined,
                }
              : undefined
          }
          notes={
            notesSource && view.canViewNotes ? (
              <CrewNotesPanel
                source={notesSource}
                view={view}
                loadVessels={() => source.vessels()}
                {...(source.brand
                  ? { brand: () => source.brand?.() ?? Promise.resolve(null) }
                  : {})}
                {...(source.pdf ? { pdf: source.pdf } : {})}
              />
            ) : undefined
          }
        />
      ) : (
        <div className={classes.page} aria-busy>
          <Skeleton height={150} radius="md" />
          <Skeleton height={36} radius="md" />
          <Skeleton height={280} radius="md" />
        </div>
      )}
      {editor && view && dialog === 'edit' ? (
        <CrewCardDialog
          mode="edit"
          view={view}
          editor={editor}
          today={view.today ?? new Date().toISOString().slice(0, 10)}
          onClose={() => setDialog(null)}
          onSaved={(saved) => {
            setDialog(null);
            setState({ kind: 'ready', view: saved });
          }}
        />
      ) : null}
      {movements && view && movement?.action === 'signOn' && vessels.length > 0 ? (
        <SignOnDialog
          editor={movements}
          people={[{ value: view.userId, label: view.fullName }]}
          userId={view.userId}
          vessels={vessels}
          canSetWage={view.canSetWage === true}
          onClose={() => setMovement(null)}
          onSaved={moved}
        />
      ) : null}
      {movements && target && movement?.action === 'signOff' ? (
        <SignOffDialog
          editor={movements}
          target={target}
          onClose={() => setMovement(null)}
          onSaved={moved}
        />
      ) : null}
      {movements && target && movement?.action === 'rankChange' ? (
        <RankChangeDialog
          editor={movements}
          target={target}
          today={viewToday}
          onClose={() => setMovement(null)}
          onSaved={moved}
        />
      ) : null}
      {movements && target && movement?.action === 'amend' ? (
        <AmendDialog
          editor={movements}
          target={target}
          tab={target.validTo === null ? 'joining' : 'sign_off'}
          today={viewToday}
          onClose={() => setMovement(null)}
          onSaved={moved}
        />
      ) : null}
      {movements && target && (movement?.action === 'cancel' || movement?.action === 'revoke') ? (
        <ReasonDialog
          editor={movements}
          target={target}
          mode={movement.action}
          today={viewToday}
          onClose={() => setMovement(null)}
          onSaved={moved}
        />
      ) : null}
      {movements && target && movement?.action === 'history' ? (
        <HistoryDialog editor={movements} target={target} onClose={() => setMovement(null)} />
      ) : null}
      {editor?.remove && view && dialog === 'remove' ? (
        <RemoveCardDialog
          view={view}
          remove={editor.remove}
          onClose={() => setDialog(null)}
          onRemoved={() => {
            setDialog(null);
            // Kart kaldırıldı: Crew listesine dönülür
            shell?.navigate('/crew');
          }}
        />
      ) : null}
      {resetPassword && view && dialog === 'reset' ? (
        // Settings ▸ Users "Reset password" ile aynı pencere ve metinler (ADR 0042 §23)
        <PasswordConfirm
          title={t('setup.users.confirm.resetPasswordTitle')}
          body={t('setup.users.confirm.resetPasswordBody', { name: view.fullName })}
          confirmLabel={t('setup.users.confirm.resetPassword')}
          result={temporary}
          onClose={() => {
            setDialog(null);
            // Satır sürümü değişti: kart yeniden okunur
            if (temporary) load();
            setTemporary(null);
          }}
          onConfirm={async (password) => {
            try {
              // Sıfırlama devre dışı kişiyi etkinleştirmez (ADR 0150 §5): sonuç bunu söyler
              setTemporary({
                name: view.fullName,
                email: view.email ?? '',
                password: await resetPassword(password),
                active: view.active,
              });
              return null;
            } catch (error) {
              return setupErrorText(t, setupErrorCode(error), setupErrorDetail(error));
            }
          }}
        />
      ) : null}
      {editor?.changeEmail && view?.email && dialog === 'changeEmail' ? (
        // Settings ▸ Users "Change login email" ile aynı pencere (ADR 0145)
        <ChangeEmailDialog
          user={{ email: view.email, fullName: view.fullName }}
          onClose={() => setDialog(null)}
          onConfirm={async (email, password) => {
            try {
              await editor.changeEmail?.(view.userId, view.rowVersion, email, password);
              accountDone(
                t('setup.users.changeEmail.notice', {
                  name: view.fullName,
                  email: email.toLowerCase(),
                }),
              );
              return null;
            } catch (error) {
              return accountError(error);
            }
          }}
        />
      ) : null}
      {editor && view && (dialog === 'disable' || dialog === 'enable' || dialog === 'resetTotp') ? (
        // Settings ▸ Users'daki aynı onay pencereleri ve metinler (ADR 0042 §23)
        <PasswordConfirm
          title={t(`setup.users.confirm.${dialog}Title`)}
          body={t(`setup.users.confirm.${dialog}Body`, { name: view.fullName })}
          confirmLabel={t(`setup.users.confirm.${dialog}`)}
          danger={dialog === 'disable'}
          onClose={() => setDialog(null)}
          onConfirm={async (password) => {
            try {
              if (dialog === 'resetTotp') {
                await editor.resetTotp?.(view.userId, password);
                accountDone(t('setup.users.totpNotice', { name: view.fullName }));
              } else {
                await editor.setLoginActive?.(
                  view.userId,
                  view.rowVersion,
                  dialog === 'enable',
                  password,
                );
                accountDone(
                  t(`setup.users.${dialog === 'enable' ? 'enabledNotice' : 'disabledNotice'}`, {
                    name: view.fullName,
                  }),
                );
              }
              return null;
            } catch (error) {
              return accountError(error);
            }
          }}
        />
      ) : null}
      {editor && view && dialog === 'login' ? (
        <CreateLoginDialog
          view={view}
          editor={editor}
          onClose={() => setDialog(null)}
          onSaved={(saved) => {
            setDialog(null);
            setState({ kind: 'ready', view: saved });
          }}
        />
      ) : null}
    </div>
  );
}
